import { useEffect } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';

// Helper to determine if a color is light or dark based on HSL lightness
function isLightBackground(hslString: string): boolean {
  const parts = hslString.split(/\s+/);
  if (parts.length >= 3) {
    const lightness = parseFloat(parts[2].replace('%', ''));
    return lightness > 50;
  }
  return false;
}

// Generate derived colors for light vs dark themes
function getDerivedColors(primary: string, secondary: string, background: string, foreground: string) {
  const isLight = isLightBackground(background);
  
  if (isLight) {
    // Light theme - derive colors for proper contrast
    return {
      card: '0 0% 100%', // White cards
      cardForeground: foreground,
      popover: '0 0% 100%',
      popoverForeground: foreground,
      primaryForeground: '0 0% 100%', // White text on primary
      secondaryForeground: '0 0% 100%', // White text on secondary
      muted: '220 14% 96%', // Light gray
      mutedForeground: '220 9% 46%', // Medium gray text
      accent: primary,
      accentForeground: '0 0% 100%',
      border: '220 13% 91%', // Light border
      input: '220 13% 91%',
      ring: primary,
      sidebarBackground: '220 14% 96%',
      sidebarForeground: foreground,
      sidebarPrimary: primary,
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '220 14% 92%',
      sidebarAccentForeground: foreground,
      sidebarBorder: '220 13% 91%',
      sidebarRing: primary,
    };
  } else {
    // Dark theme - use existing dark-friendly colors
    return {
      card: background,
      cardForeground: foreground,
      popover: background,
      popoverForeground: foreground,
      primaryForeground: foreground,
      secondaryForeground: foreground,
      muted: '215 16% 47%',
      mutedForeground: '215 20% 65%',
      accent: primary,
      accentForeground: foreground,
      border: '217 19% 27%',
      input: '217 19% 27%',
      ring: primary,
      sidebarBackground: background,
      sidebarForeground: '215 20% 65%',
      sidebarPrimary: primary,
      sidebarPrimaryForeground: foreground,
      sidebarAccent: '217 19% 27%',
      sidebarAccentForeground: foreground,
      sidebarBorder: '217 19% 27%',
      sidebarRing: primary,
    };
  }
}

export function ThemeEngine() {
  const { organization } = useOrganization();

  useEffect(() => {
    // Skip theme application on super-admin routes to preserve light theme
    if (window.location.pathname.startsWith('/super-admin')) return;

    if (!organization?.id || !organization?.settings) return;

    const { settings } = organization;
    const root = document.documentElement;

    // Core colors from settings
    const primary = settings.primary_color || '199 89% 32%';
    const secondary = settings.secondary_color || '142 76% 36%';
    const background = settings.background_color || '222 47% 11%';
    const foreground = settings.foreground_color || '210 40% 98%';

    // Apply core CSS custom properties
    root.style.setProperty('--primary', primary);
    root.style.setProperty('--secondary', secondary);
    root.style.setProperty('--background', background);
    root.style.setProperty('--foreground', foreground);

    // Get derived colors based on theme lightness
    const derived = getDerivedColors(primary, secondary, background, foreground);

    // Apply all derived colors
    root.style.setProperty('--card', derived.card);
    root.style.setProperty('--card-foreground', derived.cardForeground);
    root.style.setProperty('--popover', derived.popover);
    root.style.setProperty('--popover-foreground', derived.popoverForeground);
    root.style.setProperty('--primary-foreground', derived.primaryForeground);
    root.style.setProperty('--secondary-foreground', derived.secondaryForeground);
    root.style.setProperty('--muted', derived.muted);
    root.style.setProperty('--muted-foreground', derived.mutedForeground);
    root.style.setProperty('--accent', derived.accent);
    root.style.setProperty('--accent-foreground', derived.accentForeground);
    root.style.setProperty('--border', derived.border);
    root.style.setProperty('--input', derived.input);
    root.style.setProperty('--ring', derived.ring);
    root.style.setProperty('--sidebar-background', derived.sidebarBackground);
    root.style.setProperty('--sidebar-foreground', derived.sidebarForeground);
    root.style.setProperty('--sidebar-primary', derived.sidebarPrimary);
    root.style.setProperty('--sidebar-primary-foreground', derived.sidebarPrimaryForeground);
    root.style.setProperty('--sidebar-accent', derived.sidebarAccent);
    root.style.setProperty('--sidebar-accent-foreground', derived.sidebarAccentForeground);
    root.style.setProperty('--sidebar-border', derived.sidebarBorder);
    root.style.setProperty('--sidebar-ring', derived.sidebarRing);

    // Apply font families
    root.style.setProperty('--font-heading', settings.font_heading || 'Space Grotesk');
    root.style.setProperty('--font-body', settings.font_body || 'Inter');

    // Update document title
    if (settings.site_title) {
      document.title = settings.site_title;
    }

    // Update favicon if custom
    if (settings.favicon_url) {
      const existingFavicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (existingFavicon) {
        existingFavicon.href = settings.favicon_url;
      } else {
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = settings.favicon_url;
        document.head.appendChild(favicon);
      }
    }

    // Load custom fonts if different from defaults
    const defaultHeading = 'Space Grotesk';
    const defaultBody = 'Inter';
    
    if (settings.font_heading !== defaultHeading || settings.font_body !== defaultBody) {
      const fontsToLoad: string[] = [];
      
      if (settings.font_heading && settings.font_heading !== defaultHeading) {
        fontsToLoad.push(`family=${settings.font_heading.replace(/ /g, '+')}:wght@400;500;600;700`);
      }
      if (settings.font_body && settings.font_body !== defaultBody) {
        fontsToLoad.push(`family=${settings.font_body.replace(/ /g, '+')}:wght@400;500;600`);
      }

      if (fontsToLoad.length > 0) {
        const existingCustomFonts = document.querySelector('link[data-custom-fonts]');
        if (existingCustomFonts) {
          existingCustomFonts.remove();
        }

        const link = document.createElement('link');
        link.href = `https://fonts.googleapis.com/css2?${fontsToLoad.join('&')}&display=swap`;
        link.rel = 'stylesheet';
        link.setAttribute('data-custom-fonts', 'true');
        document.head.appendChild(link);
      }
    }

    // Cleanup function to reset to defaults when unmounting
    return () => {
      // We don't reset on unmount since the theme should persist
      // Only reset if navigating to a different org (handled by re-running effect)
    };
  }, [organization?.id, organization?.settings]);

  return null;
}
